import { observer } from '@formily/react';
import { IAnyObject, isVacuous } from '@yimoka/shared';
import { BaseStore, getEntityStore, IEntityConfig } from '@yimoka/store';
import React, { useEffect } from 'react';

import { useDeepMemo } from '../../hooks/deep-memo';
import { useEntityConfig } from '../../hooks/entity-config';

import { Entity, IEntityProps } from './base';

export const EntityAdd = observer((props: IEntityAddProps) => {
  const { config, store, scope, defaultValues, entity, instanceKey, dispose, ...args } = props;
  const curConfig = useEntityConfig(config ?? entity?.config);

  const curStore = useDeepMemo(() => {
    if (store instanceof BaseStore) {
      if (!isVacuous(defaultValues)) {
        store.setDefaultValues(defaultValues);
      }
      return store;
    }
    if (entity) {
      const options: IAnyObject = { ...(store ?? {}), instanceKey };
      if (!isVacuous(defaultValues)) {
        options.defaultValues = { ...options.defaultValues, ...defaultValues };
      }
      return entity.getAdd(options);
    }
    if (!isVacuous(defaultValues)) {
      if (!store) {
        return getEntityStore({ defaultValues }, 'add', curConfig);
      }
      store.defaultValues = { ...store.defaultValues, ...defaultValues };
    }
    return getEntityStore(store, 'add', curConfig);
  }, [store, curConfig, defaultValues, entity, instanceKey]);

  useEffect(() => {
    if (!dispose || !entity || store instanceof BaseStore) return;
    return () => {
      entity.disposeAdd(instanceKey);
    };
  }, [dispose, entity, store, instanceKey]);

  const useScope = useDeepMemo(() => ({ $entity: entity, $config: curConfig, ...scope }), [entity, curConfig, scope]);

  return <Entity {...args} scope={useScope} store={curStore} />;
});

export type IEntityAddProps<V extends object = IAnyObject, R extends object = IAnyObject> = Partial<IEntityProps<V, R>> & {
  defaultValues?: IAnyObject;
  config?: IEntityConfig<V>;
}
